/**
 * Contact Page
 * 
 * Public contact page with:
 * - Dynamic contact info from Settings
 * - Dynamic business hours from Settings
 * - Contact form that saves to Supabase
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BakerySchema } from '../components/StructuredData';
import { usePageSeo } from '../utils/seo';
import { DEFAULT_OG_IMAGE, PAGE_SEO } from '../utils/seoDefaults';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { publicCatchMessage, publicMessageFromApiErrorResponse } from '../lib/publicSiteErrorMessage';
import { BAKERY_LOCAL } from '../utils/bakeryLocal';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface BusinessHours {
  day: string;
  open: string;
  close: string;
  isClosed: boolean;
}

interface ContactInfo {
  phone: string;
  email: string;
  address: string;
}

// Format time from 24h to 12h format
function formatTime(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${hour12}${minutes ? ':' + minutes.toString().padStart(2, '0') : ''}${ampm}`;
}

// Format business hours for display
function formatHoursDisplay(hours: BusinessHours[]): string {
  const lines: string[] = [];
  
  // Group consecutive days with same hours
  let i = 0;
  while (i < hours.length) {
    const current = hours[i];
    let endIndex = i;
    
    // Find consecutive days with same hours
    while (
      endIndex < hours.length - 1 &&
      hours[endIndex + 1].isClosed === current.isClosed &&
      hours[endIndex + 1].open === current.open &&
      hours[endIndex + 1].close === current.close
    ) {
      endIndex++;
    }
    
    const startDay = current.day.substring(0, 3);
    const endDay = hours[endIndex].day.substring(0, 3);
    const dayRange = startDay === endDay ? startDay : `${startDay}-${endDay}`;
    
    if (current.isClosed) {
      lines.push(`${dayRange}: Closed`);
    } else {
      lines.push(`${dayRange}: ${formatTime(current.open)} - ${formatTime(current.close)}`);
    }
    
    i = endIndex + 1;
  }
  
  return lines.join('\n');
}

/** Default subject: General Question (value `question` in the select). No URL/query prefills — customers choose or change in the form. */
const DEFAULT_CONTACT_SUBJECT = 'question' as const;

export default function Contact() {
  const { getImage } = useSiteImageOverrides();
  usePageSeo({
    ...PAGE_SEO.contact,
    og: {
      type: 'website',
      title: PAGE_SEO.contact.title,
      description: PAGE_SEO.contact.description,
      image: getImage('og_image', DEFAULT_OG_IMAGE),
    },
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '(705) 503-4966',
    email: 'nannybbakedgoods@outlook.com',
    address: '62 Commerce Park Drive, Unit L, Barrie, ON L4N 8W8',
  });
  const [businessHours, setBusinessHours] = useState<BusinessHours[]>([]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    defaultValues: { subject: DEFAULT_CONTACT_SUBJECT },
  });

  // Fetch dynamic settings
  useEffect(() => {
    // Fetch contact info
    fetch('/api/settings/contact')
      .then((res) => res.json())
      .then((data) => {
        if (data.phone || data.email || data.address) {
          setContactInfo(data);
        }
      })
      .catch((err) => console.error('Failed to fetch contact info:', err));

    // Fetch business hours
    fetch('/api/settings/hours')
      .then((res) => res.json())
      .then((data) => {
        if (data.hours && Array.isArray(data.hours)) {
          setBusinessHours(data.hours);
        }
      })
      .catch((err) => console.error('Failed to fetch hours:', err));
  }, []);

  const onSubmit = async (data: ContactFormData) => {
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        let body: unknown = null;
        try {
          body = await response.json();
        } catch {
          body = null;
        }
        throw new Error(publicMessageFromApiErrorResponse(response.status, body));
      }

      setSubmitted(true);
      reset();
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error('Contact form submit:', err);
      setSubmitError(publicCatchMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Build contact info items for display
  const contactItems = [
    {
      icon: MapPinIcon,
      title: 'Address',
      content: contactInfo.address.replace(', ', '\n'),
      link: BAKERY_LOCAL.googleMapsUrl,
    },
    {
      icon: PhoneIcon,
      title: 'Phone',
      content: contactInfo.phone,
      link: `tel:${contactInfo.phone.replace(/\D/g, '')}`,
    },
    {
      icon: EnvelopeIcon,
      title: 'Email',
      content: contactInfo.email,
      link: `mailto:${contactInfo.email}`,
    },
    {
      icon: ClockIcon,
      title: 'Hours',
      content: businessHours.length > 0 
        ? formatHoursDisplay(businessHours)
        : 'Tue-Fri: 11am - 4:30pm\nSat: 10am - 3pm\nSun-Mon: Closed',
    },
  ];

  return (
    <>
      <BakerySchema />
    <div>
      {/* Hero Section */}
      <section className="gradient-primary text-white section-padding">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-display font-bold mb-4"
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-100 max-w-2xl mx-auto"
          >
            Have a question? Want to place an order? We'd love to hear from you!
          </motion.p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-display font-bold text-gray-900 mb-6">
                Contact Information
              </h2>
              <div className="space-y-6 mb-8">
                {contactItems.map((info) => (
                  <div key={info.title} className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                        <info.icon className="h-6 w-6 text-primary-600" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          target={info.link.startsWith('http') ? '_blank' : undefined}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-gray-600 hover:text-primary-600 transition-colors whitespace-pre-line"
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-gray-600 whitespace-pre-line">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Secondary chat — primary path is the form on this page */}
              <p className="text-sm text-gray-600 leading-relaxed">
                Prefer chat?{' '}
                <a
                  href="https://m.me/100086623726840"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary-600 hover:text-primary-700 underline-offset-2 hover:underline"
                >
                  Facebook Messenger
                </a>
                {' · '}
                <a
                  href="https://www.instagram.com/nannybbakery/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary-600 hover:text-primary-700 underline-offset-2 hover:underline"
                >
                  Instagram
                </a>
              </p>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="card p-8">
                <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                  Send us a Message
                </h2>
                <p className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                  Custom cake? Use the{' '}
                  <Link to="/cake-request" className="font-semibold underline underline-offset-2">
                    cake request form
                  </Link>{' '}
                  (size, flavour, filling, and design).
                </p>
                
                {submitted && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-semibold">
                      ✓ Thank you! Your message has been sent. We'll get back to you soon.
                    </p>
                  </div>
                )}

                {submitError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 font-semibold">
                      ✗ {submitError}
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name', { required: 'Name is required' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {errors.name && (
                      <p className="text-red-600 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {errors.email && (
                      <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      {...register('phone')}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Subject *
                    </label>
                    <select
                      id="subject"
                      {...register('subject', { required: 'Subject is required' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="question">General Question</option>
                      <option value="quote">Price quote / Menu order</option>
                      <option value="order">Place an Order</option>
                      <option value="other">Other</option>
                    </select>
                    {errors.subject && (
                      <p className="text-red-600 text-sm mt-1">{errors.subject.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      {...register('message', { required: 'Message is required' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {errors.message && (
                      <p className="text-red-600 text-sm mt-1">{errors.message.message}</p>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    disabled={submitting}
                    className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Teaser only — full Q&A + FAQ schema live on /bakery-in-barrie */}
      <section className="bg-white section-padding pt-0">
        <div className="container-custom max-w-3xl text-center">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-3">
            Common questions
          </h2>
          <p className="text-gray-700 mb-4">
            Hours, custom cakes, pickup, stuffed cookies, and gluten-free options are answered on
            our Barrie bakery page so Google can show them too.
          </p>
          <Link to="/bakery-in-barrie#common-questions" className="btn-primary">
            Common questions
          </Link>
        </div>
      </section>

      {/* Map Section */}
      <section className="bg-gray-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-display font-bold text-gray-900 mb-6 text-center">
            Find Us
          </h2>
          <div className="rounded-xl overflow-hidden shadow-xl bg-gray-200 relative" style={{ minHeight: '450px' }}>
            {/* Embed the Google Business listing, not the plaza street address. */}
            <iframe
              src={BAKERY_LOCAL.mapsEmbedUrl}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Nanny B's Bakery on Google Maps"
              className="w-full"
              onLoad={() => {
                // Hide fallback if iframe loads successfully
                const fallback = document.querySelector('.map-fallback');
                if (fallback) {
                  (fallback as HTMLElement).style.display = 'none';
                }
              }}
            />
            {/* Fallback if iframe is blocked - shown by default, hidden when iframe loads */}
            <div className="map-fallback absolute inset-0 flex flex-col items-center justify-center bg-gray-100 p-8 text-center">
              <MapPinIcon className="h-16 w-16 text-primary-600 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">View on Google Maps</h3>
              <p className="text-gray-600 mb-4 whitespace-pre-line">{contactInfo.address}</p>
              <a
                href={BAKERY_LOCAL.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Open in Google Maps
              </a>
              <p className="text-sm text-gray-500 mt-4">
                If the map doesn't load, click the button above to view our location.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
